<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '../stores/user-store'

interface CaisseOption {
  id: number
  name: string
  icon: string
  color: string
  route?: string
}

const router = useRouter()
const userStore = useUserStore()
const isOpen = ref(false)

// Masquer le panel pour les utilisateurs "Collaborateur"
const isVisible = computed(() => userStore.role !== 'Collaborateur')

const caisseOptions: CaisseOption[] = [
  {
    id: 1,
    name: 'Validation de transfert',
    icon: 'mso-history',
    color: 'primary',
    route: 'validation-transfert',
  },
  {
    id: 2,
    name: 'Validation de dépense',
    icon: 'mso-check_circle',
    color: 'info',
    route: 'validation-depense',
  },
  {
    id: 3,
    name: 'Visuelle de caisse',
    icon: 'mso-visibility',
    color: 'secondary',
    route: 'visuelle-caisse',
  },
  {
    id: 4,
    name: 'Solde en banque',
    icon: 'mso-account_balance',
    color: 'success',
    route: 'solde-en-banque',
  },
  {
    id: 5,
    name: 'Mouvement des transactions',
    icon: 'mso-compare_arrows',
    color: 'warning',
    route: 'mouvement-transactions',
  },
  {
    id: 6,
    name: 'Pilotage opérationnel',
    icon: 'mso-flight_takeoff',
    color: 'danger',
    route: 'pilotage-operationnel',
  },
]

const togglePanel = () => {
  isOpen.value = !isOpen.value
}

const handleOptionClick = (option: CaisseOption) => {
  if (option.route) {
    router.push({ name: option.route })
    isOpen.value = false
  }
}
</script>

<template>
  <div v-if="isVisible" class="settings-panel-wrapper">
    <!-- Bouton toggle avec icône engrenage -->
    <button class="toggle-button" :class="{ active: isOpen }" title="Autres paramètres" @click="togglePanel">
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    </button>

    <!-- Panneau de paramètres -->
    <Transition name="slide">
      <div v-if="isOpen" class="panel">
        <div class="panel-header">
          <h2 class="panel-title">Autre paramètre</h2>
          <button class="close-button" title="Fermer" @click="togglePanel">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="panel-content">
          <div class="section-label">INFORMATIONS DE CAISSE</div>
          <ul class="caisse-list">
            <li
              v-for="option in caisseOptions"
              :key="option.id"
              class="caisse-item"
              :class="{ clickable: option.route }"
              @click="handleOptionClick(option)"
            >
              <span class="icon" :style="{ color: `var(--va-${option.color})` }">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="8" />
                </svg>
              </span>
              <span class="label">{{ option.name }}</span>
            </li>
          </ul>
        </div>
      </div>
    </Transition>

    <!-- Overlay pour fermer au clic extérieur -->
    <Transition name="fade">
      <div v-if="isOpen" class="overlay" @click="togglePanel"></div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.settings-panel-wrapper {
  position: relative;
}

.toggle-button {
  position: fixed;
  right: 20px; /* bien visible à droite */
  bottom: 80px;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  background: var(--va-primary);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 1100;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    transform: scale(1.05);
  }

  &.active {
    background: var(--va-danger);
  }
}

.panel {
  position: fixed;
  right: 20px;
  top: 80px;
  width: 300px;
  max-height: 70vh;
  background: var(--va-background-element);
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  z-index: 1000;
}

.panel-header {
  background: linear-gradient(135deg, #5b5bff 0%, #4a4aff 100%);
  color: white;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  font-size: 1rem;
  font-weight: bold;
  margin: 0;
}

.close-button {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
}

.panel-content {
  padding: 16px;
}

.section-label {
  font-size: 0.75rem;
  font-weight: bold;
  color: var(--va-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.caisse-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.caisse-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--va-background-border);
  cursor: pointer;
  transition: background 0.2s ease;

  &.clickable {
    cursor: pointer;
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.02);
    padding-left: 4px;
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  min-width: 24px;
}

.label {
  color: var(--va-text-primary);
  font-size: 0.95rem;
  font-weight: 500;
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
}

// Animations
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
